// Helpers for computing commission splits & the 7-item deliverables checklist.

export const CHECKLIST_ITEMS = [
  { id: 'address_sent', label: 'address sent' },
  { id: 'product_received', label: 'product received' },
  { id: 'brief_received', label: 'brief received' },
  { id: 'talent_to_film', label: 'talent to film' },
  { id: 'content_sent_to_brand', label: 'content sent to brand' },
  { id: 'amends_due', label: 'amends due' },
  { id: 'content_approved', label: 'content approved' },
  { id: 'content_live', label: 'content live' },
];

export const REVENUE_VIEWS = [
  { id: 'full', label: '100%', hint: 'full deal value' },
  { id: 'talent', label: 'talent', hint: "talent's share" },
  { id: 'agency', label: 'agency', hint: 'your commission' },
];

// Normalise a deal to a list of talent ids (falls back to legacy single talent_id).
export const dealTalentIds = (deal) => {
  const ids = Array.isArray(deal?.talent_ids) ? deal.talent_ids.filter(Boolean) : [];
  if (ids.length) return ids;
  return deal?.talent_id ? [deal.talent_id] : [];
};

// Fraction of the talent-share pot that goes to a specific talent.
// If explicit talent_splits provided, use them (as % values, sum should ~100).
// Otherwise fall back to equal split.
export const talentSharePercent = (deal, talentId) => {
  const ids = dealTalentIds(deal);
  if (!ids.length || !ids.includes(talentId)) return 0;
  const splits = deal?.talent_splits || {};
  const explicit = Number(splits[talentId]);
  if (Number.isFinite(explicit) && explicit > 0) {
    const total = ids.reduce((s, id) => s + (Number(splits[id]) || 0), 0);
    if (total > 0) return explicit / total; // proportional if sums != 100
  }
  return 1 / ids.length; // equal split fallback
};

// Returns { full, talent, agency } based on deal.value and commission_percent (agency %)
export const computeSplit = (deal) => {
  const full = Number(deal?.value) || 0;
  const pct = Math.max(0, Math.min(100, Number(deal?.commission_percent) || 0));
  const agency = full * (pct / 100);
  const talent = full - agency;
  return { full, agency, talent };
};

// Effective commission: deal-level override wins, else talent default, else 0
export const effectiveCommissionPercent = (deal, talent) => {
  if (deal?.commission_percent !== null && deal?.commission_percent !== undefined && deal?.commission_percent !== '') {
    return Number(deal.commission_percent) || 0;
  }
  if (talent?.commission_percent !== null && talent?.commission_percent !== undefined && talent?.commission_percent !== '') {
    return Number(talent.commission_percent) || 0;
  }
  return 0;
};

// Compute split using effective commission (talent default fallback)
export const computeEffectiveSplit = (deal, talent) => {
  const full = Number(deal?.value) || 0;
  const pct = effectiveCommissionPercent(deal, talent);
  const agency = full * (pct / 100);
  return { full, agency, talent: full - agency, pct };
};

// Given a view id, returns the amount to display for a deal
export const amountForView = (deal, view = 'full', talent = null) => {
  const s = talent !== null ? computeEffectiveSplit(deal, talent) : computeSplit(deal);
  return s[view] ?? s.full;
};

// Per-talent amount for reports/aggregation. Distributes deal amount across talents
// by their split %. For view='full', splits the total. For view='talent', splits the
// talent-share pot. For view='agency', splits the agency commission proportionally.
export const amountForTalent = (deal, view, talentId, primaryTalent = null) => {
  const share = talentSharePercent(deal, talentId);
  if (share <= 0) return 0;
  const base = amountForView(deal, view, primaryTalent);
  return base * share;
};

// Content status — multi-select; users can tick several stages at once, but the
// most-recently-toggled-on one is what shows in the compact line preview (card/table cell).
export const CONTENT_STATUSES = [
  { id: 'chased', label: 'chased', color: '#fb923c' },
  { id: 'not_started', label: '', color: '#94a3b8' },
  { id: 'address_sent', label: 'address sent', color: '#818cf8' },
  { id: 'product_received', label: 'product received', color: '#38bdf8' },
  { id: 'brief_received', label: 'brief received', color: '#22d3ee' },
  { id: 'contract_received', label: 'contract received', color: '#93c5fd' },
  { id: 'contract_signed', label: 'contract signed', color: '#60a5fa' },
  { id: 'contract_countersigned', label: 'contract countersigned', color: '#8b5cf6' },
  { id: 'talent_to_film', label: 'talent to film', color: '#f59e0b' },
  { id: 'content_sent_to_brand', label: 'content sent to brand', color: '#a78bfa' },
  { id: 'amends_due', label: 'amends due', color: '#fb7185' },
  { id: 'content_approved', label: 'content approved', color: '#d394ab' },
  { id: 'content_live', label: 'content live', color: '#059669' },
  { id: 'awaiting_po', label: 'awaiting po', color: '#eab308' },
  { id: 'invoiced', label: 'invoiced', color: '#d394ab' },
];

// All currently-ticked content statuses for a deal, in selection order (last = most recent).
export const getContentStatusList = (deal) => {
  if (Array.isArray(deal?.content_statuses) && deal.content_statuses.length) return deal.content_statuses;
  if (deal?.content_status) return [deal.content_status];
  const c = deal?.checklist || {};
  for (let i = CONTENT_STATUSES.length - 1; i >= 0; i--) {
    const id = CONTENT_STATUSES[i].id;
    if (id !== 'not_started' && c[id]) return [id];
  }
  return [];
};

// Toggle a status on/off for a deal, returning the patch to send to updateDeal.
// The last item toggled on becomes the new line-preview status. Uses '' (not null) to
// clear content_status so the backend's partial-update filter (skips null) actually saves it.
export const toggleContentStatus = (deal, id) => {
  const list = getContentStatusList(deal);
  const isOn = list.includes(id);
  const next = isOn ? list.filter((x) => x !== id) : [...list, id];
  return { content_statuses: next, content_status: next.length ? next[next.length - 1] : '' };
};

// Read the current (line-preview) content status from a deal — the most recently selected one.
export const getContentStatus = (deal) => {
  const list = getContentStatusList(deal);
  return list.length ? list[list.length - 1] : 'not_started';
};

export const getContentStatusMeta = (id) => CONTENT_STATUSES.find((s) => s.id === id) || CONTENT_STATUSES[0];

// True when the "invoiced" content status has been ticked (manually, or auto-flipped when an
// invoice is generated for this deal from the Invoicing tab).
export const isInvoicedDeal = (deal) => getContentStatusList(deal).includes('invoiced');

// Manual invoice-status dropdown used on the Invoicing tab for deals that aren't invoiced
// in-app (no generated invoice record). Covers external tools + payment lifecycle states.
export const DEAL_INVOICE_STATUSES = [
  { id: 'to_invoice', label: 'to invoice', color: '#94a3b8' },
  { id: 'awaiting_po', label: 'awaiting po', color: '#eab308' },
  { id: 'invoiced', label: 'invoiced', color: '#059669' },
  { id: 'invoiced_free_agent', label: 'invoiced in Free Agent', color: '#a855f7' },
  { id: 'invoiced_canva', label: 'invoiced in Canva', color: '#06b6d4' },
  { id: 'overdue', label: 'overdue', color: '#ef4444' },
  { id: 'paid_out', label: 'paid out', color: '#3fa172' },
];

export const dealInvoiceStatusMeta = (id) => DEAL_INVOICE_STATUSES.find((s) => s.id === id) || DEAL_INVOICE_STATUSES[0];

// Statuses that mean "done" for the purposes of dropping off the ready-to-invoice list.
export const isDealInvoiceStatusDone = (status) => ['invoiced', 'invoiced_free_agent', 'invoiced_canva', 'paid_out'].includes(status);

// Invoicing board columns (status lanes).
export const INVOICE_COLUMNS = [
  { id: 'to_invoice', label: 'to invoice', color: '#94a3b8' },
  { id: 'awaiting_po', label: 'awaiting po', color: '#eab308' },
  { id: 'invoiced', label: 'invoiced', color: '#059669' },
  { id: 'overdue', label: 'overdue', color: '#ef4444' },
  { id: 'paid_out', label: 'paid out', color: '#3fa172' },
];

const _pastDue = (iso) => {
  if (!iso) return false;
  const d = new Date(iso + 'T00:00:00');
  return !Number.isNaN(d.getTime()) && d < new Date(new Date().toDateString());
};

// Which invoicing board column a deal belongs to — derived so a deal moves to "overdue"
// automatically when its invoice lapses, and to "paid out" automatically when paid_out_at is set.
export const dealInvoiceColumn = (deal, dealInvoices = []) => {
  if (deal.paid_out_at) return 'paid_out';
  const splits = deal.invoice_splits || [];
  const splitInvoiceIds = new Set(splits.map((s) => s.invoice_id).filter(Boolean));
  const singleInv = dealInvoices.find((i) => !splitInvoiceIds.has(i.id));
  const overdue = deal.invoice_status === 'overdue'
    || (singleInv && singleInv.status === 'overdue')
    || (singleInv && singleInv.status !== 'paid' && _pastDue(singleInv.due_date))
    || (deal.split_invoicing && splits.some((s) => { const inv = dealInvoices.find((i) => i.id === s.invoice_id); return inv && inv.status !== 'paid' && (inv.status === 'overdue' || _pastDue(inv.due_date)); }));
  if (overdue) return 'overdue';
  const invoiced = deal.split_invoicing
    ? (splits.length > 0 && splits.every((s) => s.status === 'invoiced'))
    : (Boolean(singleInv) || isDealInvoiceStatusDone(deal.invoice_status));
  if (invoiced) return 'invoiced';
  if (deal.invoice_status === 'awaiting_po') return 'awaiting_po';
  return 'to_invoice';
};

// Retainer deals track multiple deliverables, each with its own content status + live date.
export const emptyDeliverable = () => ({
  id: 'dlv_' + (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`),
  title: '',
  content_status: '',
  content_statuses: [],
  go_live_date: '',
  due_date: '',
  caption: '',
  notes: '',
  ready_to_invoice: false,
  platforms: [],
  value: 0,
});

export const DELIVERABLE_PLATFORM_PRESETS = ['IG', 'TT', 'IG Stories', 'YT Short', 'YT Feed', 'UGC', 'Usage'];

// For retainer deals, the invoicing suggestion amount should be the sum of only the
// deliverables that are actually ready (ticked ready_to_invoice, or content_live), not the
// deal's whole total — falls back to the plain deal value for non-retainer deals.
export const suggestedInvoiceAmount = (deal) => {
  const deliverables = deal?.deliverables || [];
  if (deal?.retainer && deliverables.length > 0) {
    const readySum = deliverables
      .filter((d) => d.ready_to_invoice || (d.content_statuses || []).includes('content_live'))
      .reduce((sum, d) => sum + (Number(d.value) || 0), 0);
    if (readySum > 0) return readySum;
  }
  return Number(deal?.value) || 0;
};

export const checklistProgress = (deal) => {
  const c = deal?.checklist || {};
  const total = CHECKLIST_ITEMS.length;
  const done = CHECKLIST_ITEMS.filter((it) => c[it.id]).length;
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
};

export const checklistEnabled = (deal) => {
  const c = deal?.checklist || {};
  return c.__enabled === true;
};

// A deal is "stale" when it hasn't been touched in 5+ days and isn't contracted/complete yet.
// Invoiced deals never show stale — the mint "invoiced" highlight takes priority.
export const STALE_DAYS = 5;
export const isStaleDeal = (deal) => {
  if (!deal || ['contracted', 'complete'].includes(deal.stage)) return false;
  if (isInvoicedDeal(deal)) return false;
  const ts = deal.updated_at || deal.created_at;
  if (!ts) return false;
  const last = new Date(ts).getTime();
  if (Number.isNaN(last)) return false;
  const days = (Date.now() - last) / (1000 * 60 * 60 * 24);
  return days >= STALE_DAYS;
};
